import type { ScanReviewGroup } from "@hobbyshopify/backend-client";
import type { Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import {
  confirmGroup,
  confirmGroups,
  loadReviewQueue,
  pickGroupCandidate,
  rejectGroup,
} from "../../core/ops/review-ops";
import { errorText } from "../../core/primitives/errors";
import {
  batchEntry,
  isScanning,
  liveDrafts,
  reviewEntry,
  reviewQuantity,
  withoutDrafts,
} from "../../core/primitives/review-row";
import { type Capture, type ScanSession, scanIdOf, withKept } from "../../core/services/scan-session";
import { CONFIRM_ALL_KEY, DraftsActions, DraftsState } from "./drafts-model";

export type DraftsDeps = Pick<
  AppDeps,
  "client" | "authHandler" | "storeRepository" | "providersRepository" | "reviewQueueRepository" | "scanSessions"
>;

/** A thrown call, kept to be handled like an answer, then thrown on. */
const kept = (thrown: unknown) => ({ thrown });

export interface DraftsRouting {
  /** Opens a scan's review, with the list's scans to walk on from it. */
  reviewScan(scanId: string, queue: readonly string[]): void;
}

/**
 * The review queue: scans waiting for a person.
 *
 * Nothing a scan produced is in the catalogue yet: confirming a group here is
 * what creates the card. The queue is grouped by the identity each scan read
 * as, so ten copies of one card are one row, one confirm and one lot of ten.
 * Each card this browser is scanning for the shop sits at the top of that same
 * list, as iOS's, and is replaced by its group once the queue read after it lands.
 */
export class DraftsController extends SubtreeController implements DraftsActions {
  readonly state: DraftsState;
  readonly now: () => Date;
  /** Actions waiting for the queue to be read again after them (`#fresh`). */
  #waiting: Array<() => void> = [];
  #session: ScanSession | null = null;
  /** The session's cards seen landed, and those still listed until the queue is read after them. */
  readonly #seenLanded = new Set<string>();
  readonly #landed = new Set<string>();
  /** Ready cards with a row, as last seen: a row outlives the session letting its card go. */
  readonly #kept = new Map<string, Capture>();
  /** Scans a group shown here held: their cards get no row of their own again, even when a later read holds it back. */
  readonly #shownScans = new Set<string>();

  constructor(
    readonly deps: DraftsDeps,
    readonly routing: DraftsRouting,
    { now = () => new Date() }: { now?: () => Date } = {},
  ) {
    super();
    this.now = now;
    this.state = new DraftsState();
    this.subtree.put(DraftsState, this.state);
    this.subtree.put(DraftsActions, this);

    this.sync(() => this.#loadIfNeeded(), [deps.storeRepository]);

    // The cached queue shows at once; a stale mark (a confirm elsewhere, a scan
    // landing) fetches it again behind the rows. A group whose newest scan
    // is still in the pipeline is no row yet. A fresh queue takes the drafts of
    // the groups it no longer lists — confirmed here, partway through a batch,
    // or anywhere else — but not of one only hidden while a copy is in flight,
    // and lets go of the actions waiting for it.
    this.sync(() => {
      const repo = deps.reviewQueueRepository;
      this.state.groups.value = repo.groups.filter((g) => !isScanning(g.sample, this.now()));
      this.state.unrecognised.value = repo.unrecognised;
      this.state.status.value = repo.status;
      if (repo.status.kind === "loaded" && !repo.isStale) {
        this.state.drafts.update((d) =>
          liveDrafts(
            d,
            repo.groups.map((g) => g.key),
          ),
        );
      }
      if (this.#queueSettled()) {
        this.#release();
        // What landed is read now, listed or not (another device may have confirmed it meanwhile).
        this.#landed.clear();
      }
      // A card's row goes in the render that lists its group.
      if (this.#session !== null) this.#mirror();
      this.#loadIfNeeded();
    }, [deps.reviewQueueRepository]);
    this.autoDispose(() => this.#release());

    this.sync(() => this.#follow(), [deps.storeRepository]);
    this.autoDispose(() => this.#session?.removeListener(this.#mirror));
  }

  confirm = (group: ScanReviewGroup) => {
    const entry = reviewEntry(group, this.state.drafts.value[group.key]);
    if (!entry) return Promise.resolve();
    return this.#act(
      group.key,
      () => [group],
      "Confirm failed.",
      (shopId) => confirmGroup({ ...this.deps, shopId, entry }),
    );
  };

  reject = (group: ScanReviewGroup) =>
    this.#act(
      group.key,
      () => [group],
      "Reject failed.",
      (shopId) => rejectGroup({ ...this.deps, shopId, group }),
    );

  confirmAllPriced = () => {
    const drafts = this.state.drafts.value;
    const entries = this.state.groups.value.flatMap((g) => batchEntry(g, drafts[g.key]) ?? []);
    if (entries.length === 0) return Promise.resolve();
    let confirmed = 0;
    return this.#act(
      CONFIRM_ALL_KEY,
      // The rows sent: each one confirmed, and the one on its way when a call threw.
      () => entries.slice(0, confirmed + 1).map((e) => e.group),
      "Confirm failed.",
      (shopId) =>
        confirmGroups({
          ...this.deps,
          shopId,
          entries,
          onConfirmed: (entry) => {
            confirmed++;
            this.#settled(entry.group.key);
          },
        }),
    );
  };

  setPrice = (group: ScanReviewGroup, price: string) => {
    this.state.drafts.update((d) => ({ ...d, [group.key]: { ...d[group.key], price } }));
  };

  setQuantity = (group: ScanReviewGroup, quantity: number) => {
    const next = Math.max(0, Math.floor(quantity));
    if (next === reviewQuantity(group, this.state.drafts.value[group.key])) return;
    this.state.drafts.update((d) => ({ ...d, [group.key]: { ...d[group.key], quantity: next } }));
  };

  setApproveCodes = (group: ScanReviewGroup, approveCodes: boolean) => {
    this.state.drafts.update((d) => ({ ...d, [group.key]: { ...d[group.key], approveCodes } }));
  };

  pick = async (group: ScanReviewGroup, productId: string) => {
    const shopId = this.deps.storeRepository.shopId;
    const busy = this.state.busyKey.value;
    if (shopId === null || this.state.picking.value !== null || busy === group.key || busy === CONFIRM_ALL_KEY) return;
    this.state.picking.value = { key: group.key, productId };
    this.state.actionError.value = null;
    try {
      const r = await pickGroupCandidate({ ...this.deps, shopId, group, productId }).catch(kept);
      if ("thrown" in r) {
        if (!this.isDisposed) {
          this.state.actionError.value = "Could not switch the match.";
          await this.#fresh();
        }
        throw r.thrown;
      }
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.actionError.value = errorText(r.errors) || "Could not switch the match.";
      }
      await this.#fresh();
    } finally {
      if (!this.isDisposed) this.state.picking.value = null;
    }
  };

  review = (group: ScanReviewGroup) => {
    this.routing.reviewScan(
      group.sample.id,
      this.state.groups.value.map((g) => g.sample.id),
    );
  };

  dismissError = () => {
    this.state.actionError.value = null;
  };

  retry = () => {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId !== null)
      void loadReviewQueue({ ...this.deps, shopId, locationId: this.deps.storeRepository.locationId });
  };

  retryCapture = (id: string) => {
    this.#session?.retry(id);
  };

  discardCapture = (id: string) => {
    this.#session?.discard(id);
  };

  /** The session of the shop × counter chosen: what this browser scans for the shop lands here. */
  #follow(): void {
    if (this.isDisposed) return;
    const { storeRepository, scanSessions } = this.deps;
    const shopId = storeRepository.shopId;
    const session =
      shopId === null ? null : scanSessions.for({ shopId, locationId: storeRepository.locationId, customerId: null });
    if (session === this.#session) return;
    this.#session?.removeListener(this.#mirror);
    this.#session = session;
    this.#seenLanded.clear();
    this.#landed.clear();
    this.#kept.clear();
    this.#shownScans.clear();
    session?.addListener(this.#mirror);
    this.#mirror();
  }

  /**
   * The session's cards on their way, newest first, as iOS lists them over the queue. A card that lands is marked in
   * the queue before it turns ready, so it keeps its row until the queue read after it lands, and does not blink out
   * of the list meanwhile; one already landed when this page saw it is read already.
   */
  #mirror = () => {
    const session = this.#session;
    if (session === null) {
      this.state.captures.value = [];
      return;
    }
    // A card whose fate was unknown and whose scan the queue lists is read again: the session's notice runs this again.
    // The scans no card was recognised in are listed with the queue, apart from its groups.
    const repo = this.deps.reviewQueueRepository;
    const cached = new Set([...repo.groups.flatMap((g) => g.scanIds), ...repo.unrecognised.map((s) => s.id)]);
    let asked = false;
    for (const capture of session.captures) {
      if (capture.unsure && cached.has(scanIdOf(capture) ?? "")) asked = session.listed(capture.id) || asked;
    }
    if (asked) return;
    const read = this.#queueSettled();
    for (const capture of session.captures) {
      if (capture.status !== "ready") continue;
      this.#kept.set(capture.id, capture);
      if (this.#seenLanded.has(capture.id)) continue;
      this.#seenLanded.add(capture.id);
      if (!read) this.#landed.add(capture.id);
    }
    const all = withKept(session.captures, this.#kept);
    for (const g of this.state.groups.value) for (const id of g.scanIds) this.#shownScans.add(id);
    for (const scan of this.state.unrecognised.value) this.#shownScans.add(scan.id);
    // A read that answered between two landings lists the first: its row goes then, not with the second's read.
    for (const capture of all) {
      const scanId = scanIdOf(capture);
      if (this.#landed.has(capture.id) && scanId !== null && cached.has(scanId)) this.#landed.delete(capture.id);
    }
    // One row per card: none of its own once a row of the list has shown it, even while a later read holds its group
    // back. A landed card keeps its row while the read after it is on its way (under its error when it fails), and
    // while its group is held back by a copy still in the pipeline, though the session has let the card go.
    // A card that failed after a group showed it has a row again: its group says nothing of the failure, as iOS lists
    // every unfinished capture.
    const rows = all.filter((c) => {
      const scanId = scanIdOf(c);
      if (c.status !== "failed" && scanId !== null && this.#shownScans.has(scanId)) return false;
      if (c.status !== "ready") return true;
      return this.#landed.has(c.id) || (scanId !== null && cached.has(scanId));
    });
    for (const id of this.#kept.keys()) if (!rows.some((r) => r.id === id)) this.#kept.delete(id);
    this.state.captures.value = rows;
  };

  /**
   * One confirm or reject at a time, and none for a row whose match is being
   * switched: it would go through at the old candidate's price, and the switch
   * would then fail on scans already confirmed. Busy until the queue on screen
   * is read again after it, whatever it answered (`#fresh`): a call that threw
   * may have gone through, so that is said and read again before it is thrown
   * on. A row it settled takes its draft with it at once, as on iOS, since a
   * newer copy of the card can carry the same key into the next read.
   */
  async #act(
    key: string,
    sent: () => readonly ScanReviewGroup[],
    fallback: string,
    op: (shopId: string) => Promise<Result<void, Error>>,
  ): Promise<void> {
    const shopId = this.deps.storeRepository.shopId;
    const picking = this.state.picking.value;
    if (shopId === null || this.state.busyKey.value !== null) return;
    if (picking !== null && (key === CONFIRM_ALL_KEY || picking.key === key)) return;
    this.state.busyKey.value = key;
    this.state.actionError.value = null;
    try {
      const r = await op(shopId).catch(kept);
      if ("thrown" in r) {
        if (!this.isDisposed) {
          this.state.actionError.value = fallback;
          await this.#fresh();
          if (!this.isDisposed) this.#settleGone(sent());
        }
        throw r.thrown;
      }
      if (this.isDisposed) return;
      this.state.actionError.value = r.ok ? null : errorText(r.errors) || fallback;
      if (r.ok) this.#settled(key);
      // A refusal may say the rows changed elsewhere ("already confirmed"), so
      // they are read again whatever the answer, as on a customer's page.
      if (!r.ok) this.deps.reviewQueueRepository.markStale();
      await this.#fresh();
    } finally {
      if (!this.isDisposed) this.state.busyKey.value = null;
    }
  }

  /**
   * Resolves once the queue has been read again since an action marked it stale,
   * and answered. Until then the action's row stays busy, so it is neither
   * confirmed at the price it showed before a switch nor offered again once
   * confirmed. A failed read holds it too — the rows on screen are the last good
   * read, under "Couldn't load" — until Retry or the next stale mark reads them.
   * Let go on dispose.
   */
  #fresh(): Promise<void> {
    if (this.isDisposed || this.#queueSettled()) return Promise.resolve();
    return new Promise((resolve) => this.#waiting.push(resolve));
  }

  /** A row an action settled takes its draft with it; Confirm all priced calls this as it goes, maybe after dispose. */
  #settled(key: string): void {
    if (!this.isDisposed) this.state.drafts.update((d) => withoutDrafts(d, [key]));
  }

  /**
   * After a call that threw and the read that followed: a row none of whose
   * scans the queue lists any more went through after all, and takes its draft.
   */
  #settleGone(groups: readonly ScanReviewGroup[]): void {
    const listed = new Set(this.deps.reviewQueueRepository.groups.flatMap((g) => g.scanIds));
    for (const group of groups) if (!group.scanIds.some((id) => listed.has(id))) this.#settled(group.key);
  }

  #queueSettled(): boolean {
    const repo = this.deps.reviewQueueRepository;
    return repo.status.kind === "loaded" && !repo.isStale;
  }

  #release(): void {
    const waiting = this.#waiting;
    this.#waiting = [];
    for (const resolve of waiting) resolve();
  }

  #loadIfNeeded(): void {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId === null) return;
    const repo = this.deps.reviewQueueRepository;
    if (repo.status.kind === "idle" || repo.isStale) {
      void loadReviewQueue({ ...this.deps, shopId, locationId: this.deps.storeRepository.locationId });
    }
  }
}
